import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { User, Phone, Mail, Check, ChevronDown, AlertTriangle, Edit3 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { formatUsPhone, isValidUsPhone } from '@/lib/phone';

export const ProfilePopover = () => {
  const { user, updateProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(formatUsPhone(user?.phone || ''));
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    setName(user?.name || '');
    setPhone(formatUsPhone(user?.phone || ''));
  }, [user?.name, user?.phone]);

  useEffect(() => {
    const onOpenEditor = () => {
      setOpen(true);
      setEditing(true);
    };
    window.addEventListener('open-profile-editor', onOpenEditor);
    return () => window.removeEventListener('open-profile-editor', onOpenEditor);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setEditing(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const hasPhone = !!(user?.phone && user.phone.trim());

  const handleSave = async () => {
    // Empty phone is allowed (falls back to company hotline). Non-empty must validate.
    if (phone.trim() && !isValidUsPhone(phone)) {
      toast.error('Enter a full 10-digit US phone number');
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        name: name.trim() || undefined,
        phone: phone.trim() ? phone : '',
      });
      toast.success('Profile updated');
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const initials = (user?.name || user?.email || '?')
    .split(/[\s@]+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join('') || '?';

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 text-sm font-bold uppercase tracking-wide transition-colors relative"
        data-testid="profile-popover-btn"
      >
        <span className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-[11px]">{initials}</span>
        <span className="hidden sm:inline">Profile</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        {!hasPhone && (
          <span
            className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-zinc-900"
            title="No US phone set — loads use company hotline"
            data-testid="profile-phone-missing-dot"
          />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-white border border-zinc-200 shadow-xl z-50"
          data-testid="profile-popover"
        >
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-black">{initials}</div>
            <div className="min-w-0">
              <div className="font-black text-zinc-900 truncate">{user?.name || 'Unnamed'}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>

          {!hasPhone && !editing && (
            <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 text-[12px] text-amber-800 flex gap-2" data-testid="profile-no-phone-warning">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Add your US phone.</strong> Loads you post right now show the company hotline
                instead of your direct number.
              </div>
            </div>
          )}

          <div className="p-5 space-y-4">
            {!editing ? (
              <>
                <Row icon={Mail} label="Email" value={user?.email || '—'} muted />
                <Row icon={User} label="Name" value={user?.name || 'Unnamed'} />
                <Row
                  icon={Phone}
                  label="US Phone"
                  value={hasPhone ? user.phone : 'Not set'}
                  emphasize={hasPhone}
                  warn={!hasPhone}
                />
                <button
                  onClick={() => setEditing(true)}
                  className="w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white py-2.5 text-xs font-bold uppercase tracking-wider"
                  data-testid="profile-edit-btn"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Edit
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">US Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatUsPhone(e.target.value))}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none font-mono"
                    data-testid="profile-phone-input"
                  />
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Shown on the public load board for carriers to reach you directly. Leave blank to use the company hotline.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditing(false); setName(user?.name || ''); setPhone(formatUsPhone(user?.phone || '')); }}
                    className="flex-1 border border-zinc-300 text-zinc-700 hover:border-zinc-400 py-2 text-xs font-bold uppercase tracking-wider"
                    data-testid="profile-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                    data-testid="profile-save-btn"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, value, muted, emphasize, warn }) => (
  <div className="flex items-center gap-3">
    <Icon className={`w-4 h-4 flex-shrink-0 ${warn ? 'text-amber-500' : 'text-zinc-400'}`} />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">{label}</div>
      <div className={`text-sm truncate ${
        muted ? 'text-zinc-500' : warn ? 'text-amber-700 font-bold' : emphasize ? 'text-zinc-900 font-bold font-mono' : 'text-zinc-900'
      }`}>
        {value}
      </div>
    </div>
  </div>
);
